import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import useSortableTable from "../../hooks/useSortableTable";
import { useNavigate } from "react-router-dom";
import RowActionsMenu from "../UI/RowActionsMenu";
import EmptyState from "../UI/EmptyState";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import EditAppearanceDialog from "./EditAppearanceDialog";
import DeleteAppearanceDialog from "./DeleteAppearanceDialog";

const AppearanceTable = (props) => {
  const navigate = useNavigate();
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  const rows = props.appearances.map((appearance) => ({
    id: appearance.id,
    player: appearance.player,
    playerName: appearance.player.name,
    team: appearance.team,
    goals: appearance.goals,
    assists: appearance.assists,
    passes: appearance.passes,
    interceptions: appearance.interceptions,
    tackles: appearance.tackles,
    yellowCards: appearance.yellowCards,
    redCards: appearance.redCards,
  }));

  const appearancesWithPermission = props.appearancesWithPermission.map(
    (a) => a.id
  );

  const { sorted, sortKey, sortDir, handleSort } = useSortableTable(rows, "goals", "desc");

  if (rows.length === 0) {
    return <EmptyState icon={<SportsSoccerIcon />} message="No appearances recorded." />;
  }

  return (
    <>
      <TableContainer
        className="tableContainer"
        data-testid="appearanceTable"
        component={Paper}
        sx={{ overflowX: "auto" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {appearancesWithPermission.length > 0 && <TableCell sx={{ width: 48, p: 0.5 }} />}
              <TableCell sortDirection={sortKey === "playerName" ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === "playerName"}
                  direction={sortKey === "playerName" ? sortDir : "asc"}
                  onClick={() => handleSort("playerName")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === "goals" ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === "goals"}
                  direction={sortKey === "goals" ? sortDir : "asc"}
                  onClick={() => handleSort("goals")}
                >
                  Goals
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === "assists" ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === "assists"}
                  direction={sortKey === "assists" ? sortDir : "asc"}
                  onClick={() => handleSort("assists")}
                >
                  Assists
                </TableSortLabel>
              </TableCell>
              <TableCell>Passes</TableCell>
              <TableCell>Interceptions</TableCell>
              <TableCell>Tackles</TableCell>
              <TableCell>Yellow Cards</TableCell>
              <TableCell>Red Cards</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow
                data-testid={"appearanceTableRow" + row.id}
                hover
                key={row.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}
                onClick={() => navigate(`/appearance/${row.id}`)}
              >
                {appearancesWithPermission.length > 0 && (
                  <TableCell sx={{ width: 48, p: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    {appearancesWithPermission.includes(row.id) && (
                      <RowActionsMenu
                        onEdit={() => setEditRow(row)}
                        onDelete={() => setDeleteRow(row)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>{row.player.name}</TableCell>
                <TableCell>{row.goals}</TableCell>
                <TableCell>{row.assists}</TableCell>
                <TableCell>{row.passes}</TableCell>
                <TableCell>{row.interceptions}</TableCell>
                <TableCell>{row.tackles}</TableCell>
                <TableCell>{row.yellowCards}</TableCell>
                <TableCell>{row.redCards}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editRow && (
        <EditAppearanceDialog
          open
          setOpen={(open) => !open && setEditRow(null)}
          appearance={editRow}
        />
      )}
      {deleteRow && (
        <DeleteAppearanceDialog
          open
          setOpen={(open) => !open && setDeleteRow(null)}
          appearance={deleteRow}
        />
      )}
    </>
  );
};

export default AppearanceTable;
